import { age } from "./body";
import { addDays, dayLabel } from "./date";
import { OLDER_AGE, older, type DeviceAnswers } from "./eating";
import { CONDITION_WORDS, MED_WORDS, PREGNANCY_AGES, cap, join, type Opt } from "./eatingQuestions";
import { RETEST_DAYS, chairStandLine, latestTests, placement } from "./fitnessTests";
import type {
  DayTests,
  EatingAnswers,
  EatingCondition,
  HaveKey,
  JointAnswer,
  JointKey,
  KayaState,
  SessionKey,
  TrainingAnswers,
  TrainingGoal,
  TrainingProfile,
} from "./types";

/**
 * "Plan my exercise": which questions a person is asked, how they are numbered, what each option
 * says it changes, the answers read back, the plan's one line and the rules the safety items set.
 * The machinery is `eatingQuestions.ts`'s; the answers are shared with it rather than asked twice
 * — what food holds is read back on one "Still right?" step and changed through
 * `setEatingProfile`. (ADR-053; plan 2026-10-05 §3.5, §3.6, §3.9; the Training* boards)
 *
 * Numbering (EXAMPLE-DATA "Training for everyone"): the progress line counts only the screens this
 * person sees. The joints follow-up opens inline under the chips but counts as its own step, like
 * food's low-sugar one; the BP, feet and eyes follow-ups on "Still right?" and "which meals?"
 * under When do not. The tests count as one step. So the main person sees 12, the parent at 65
 * with a knee 15, and a father of 70 on BP tablets with no joint 14.
 */

/* ---------- age bands (plan §3.9) ---------- */

export type TrainingBand = "<40" | "40-59" | "60+";
/** where "used to hard effort?" starts (DGI 2024 from 40; PAR-Q+ from 45) */
export const VIGOROUS_ASK_AGE = 40;

/** The band, matching `eating.ts::OLDER_AGE`; null without a year of birth. */
export function trainingBand(S: KayaState, today = new Date()): TrainingBand | null {
  const y = age(S, today);
  if (y === null) return null;
  return y >= OLDER_AGE ? "60+" : y >= VIGOROUS_ASK_AGE ? "40-59" : "<40";
}

/**
 * Whether to ask "used to hard effort?" before this session: 40–59, a day with fast work (B's
 * intervals, the treadmill's fast phases), and not answered yet. Asked once, as a Notice, never a
 * questionnaire step. 60+ is not asked: the Steady programme has no fast work to ask about.
 */
export function askVigorous(S: KayaState, key: SessionKey, today = new Date()): boolean {
  return (key === "B" || key === "T") && trainingBand(S, today) === "40-59" && S.profile.training?.vigorousOk === undefined;
}

/* ---------- the steps ---------- */

export type StepId =
  | "still"
  | "health"
  | "before"
  | "recent"
  | "goals"
  | "active"
  | "days"
  | "when"
  | "have"
  | "joints"
  | "jointHow"
  | "falls"
  | "body"
  | "breathless"
  | "style"
  | "tests";

/** A screen is a step, except the joints follow-up, which lives on the joints screen. */
export type ScreenId = Exclude<StepId, "jointHow">;

/** The answers as the screens edit them: a joint is ticked before its "how much?" is picked. */
export type TrainingDraftAnswers = Omit<TrainingAnswers, "joints"> & {
  joints?: Partial<Record<JointKey, Partial<JointAnswer>>>;
};

/**
 * Who is answering. `subject`: someone filling the answers in for a linked person — the two
 * safety screens are theirs alone and are not shown (ADR-042's reasoning). `gate`: this phone's
 * safety answer holds the plan, so the tests are not offered.
 */
export interface Asking {
  subject?: boolean;
  gate?: boolean;
}

/** Joints that have a left and a right. */
export const SIDED: readonly JointKey[] = ["shoulder", "hip", "knee", "ankle", "wrist"];

function ctx(S: KayaState, a: TrainingDraftAnswers, today: Date) {
  const e = S.profile.eating ?? {};
  const conds: EatingCondition[] = e.conditions ?? [];
  return {
    years: age(S, today),
    older: older(S, today),
    conds,
    anyCondition: conds.length > 0,
    joint: Object.keys(a.joints ?? {}).length > 0,
    pregnant: e.pregnant === "pregnant",
  };
}

/** The chair question is asked at 60+ or with a joint; the floor question of everyone. */
export function chairAsked(S: KayaState, a: TrainingDraftAnswers, today = new Date()): boolean {
  const c = ctx(S, a, today);
  return c.older || c.joint;
}

/** The counted steps this person will see, in order (plan §3.6). */
export function trainingSteps(
  S: KayaState,
  a: TrainingDraftAnswers,
  today = new Date(),
  asking: Asking = {}
): StepId[] {
  const c = ctx(S, a, today);
  const out: StepId[] = [S.profile.eating?.at !== undefined ? "still" : "health"];
  if (!asking.subject) out.push("before", "recent");
  out.push("goals", "active", "days", "when", "have", "joints");
  if (c.joint) out.push("jointHow");
  if (c.older || c.joint || c.conds.includes("osteoporosis")) out.push("falls");
  out.push("body");
  // an unknown age is asked: the cautious reading of not knowing
  if (c.years === null || c.years >= VIGOROUS_ASK_AGE || c.anyCondition) out.push("breathless");
  out.push("style");
  if (!c.pregnant && (asking.subject || !asking.gate)) out.push("tests");
  return out;
}

export function trainingScreens(
  S: KayaState,
  a: TrainingDraftAnswers,
  today = new Date(),
  asking: Asking = {}
): ScreenId[] {
  return trainingSteps(S, a, today, asking).filter((s): s is ScreenId => s !== "jointHow");
}

/** "Question 9 of 15", as numbers; null for a step this person does not see. */
export function trainingCount(
  S: KayaState,
  a: TrainingDraftAnswers,
  id: StepId,
  today = new Date(),
  asking: Asking = {}
): { n: number; of: number } | null {
  const steps = trainingSteps(S, a, today, asking);
  const i = steps.indexOf(id);
  return i < 0 ? null : { n: i + 1, of: steps.length };
}

/** The goals this person is offered: blood sugar and BP only with the condition. */
export function goalsOffered(S: KayaState): TrainingGoal[] {
  const conds = S.profile.eating?.conditions ?? [];
  return GOAL_OPTS.map((o) => o.value).filter(
    (g) => (g !== "sugar" || conds.includes("diabetes") || conds.includes("prediabetes")) && (g !== "bp" || conds.includes("bp"))
  );
}

/**
 * The answers as they will be saved, as a patch for `setTrainingProfile`: an answer to a question
 * this person is no longer asked is written as `undefined`, which takes it back to "not asked" —
 * the falls answer once the knee is unticked under 60, the BP follow-ups once BP is gone, a goal
 * whose condition went — so no rule reads an answer the screens no longer show. A joint without
 * its "how much?" is not an answer yet and is left out.
 */
export function pruneTraining(
  S: KayaState,
  a: TrainingDraftAnswers,
  today = new Date(),
  asking: Asking = {}
): TrainingAnswers {
  const steps = new Set(trainingSteps(S, a, today, asking));
  const conds = S.profile.eating?.conditions ?? [];
  const out: TrainingAnswers = { ...(a as TrainingAnswers) };

  if (a.joints !== undefined) {
    const joints: Partial<Record<JointKey, JointAnswer>> = {};
    for (const [k, v] of Object.entries(a.joints) as [JointKey, Partial<JointAnswer> | undefined][]) {
      if (!v?.how) continue;
      joints[k] = SIDED.includes(k) && v.side ? { how: v.how, side: v.side } : { how: v.how };
    }
    out.joints = joints;
  }
  if (a.goals !== undefined) {
    const offered = goalsOffered(S);
    out.goals = a.goals.filter((g) => offered.includes(g)).slice(0, 2);
  }
  if (out.when !== "meals") out.mealWalks = undefined;
  if (!steps.has("falls")) {
    out.falls = undefined;
    out.unsteady = undefined;
  }
  if (!chairAsked(S, a, today)) {
    out.chairNoHands = undefined;
    out.chairHelp = undefined;
  }
  if (!steps.has("breathless")) out.breathless = undefined;
  if (!conds.includes("bp")) {
    out.bpControlled = undefined;
    out.bpReading = undefined;
  }
  if (!conds.includes("diabetes")) {
    out.feet = undefined;
    out.eyesTreated = undefined;
  }
  return out;
}

/**
 * What Save writes (ADR-053): the pruned answers, the programme when none is set — Steady at 60+,
 * otherwise Kaya, so the owner's programme does not change unless he picks Steady — and `setBy`:
 * the writer's name when someone answered for this person, cleared when they answered themselves.
 */
export function trainingPatch(
  S: KayaState,
  a: TrainingDraftAnswers,
  today = new Date(),
  opts: { asking?: Asking; by?: string } = {}
): TrainingAnswers {
  const out = pruneTraining(S, a, today, opts.asking);
  out.programme = S.profile.training?.programme ?? a.programme ?? (older(S, today) ? "steady" : "kaya");
  out.setBy = opts.by;
  return out;
}

/* ---------- answered, for the disabled Next and the "N of M answered" rows ---------- */

/** Whether a saved (or draft) step has its answer. The two safety steps read `safetyAnswered`. */
export function stepAnswered(
  S: KayaState,
  a: TrainingDraftAnswers,
  id: StepId,
  today = new Date(),
  extra: { safetyAnswered?: boolean; testsDone?: boolean } = {}
): boolean {
  const conds = S.profile.eating?.conditions ?? [];
  switch (id) {
    case "still":
    case "health":
      return (
        (S.profile.eating?.conditions !== undefined || !!S.profile.eating?.conditionsPrivate) &&
        (!conds.includes("bp") || !!a.bpControlled) &&
        (!conds.includes("diabetes") || (!!a.feet && a.eyesTreated !== undefined))
      );
    case "before":
    case "recent":
      return !!extra.safetyAnswered;
    case "goals":
      return !!a.goals?.length;
    case "active":
      return !!a.active;
    case "days":
      return !!a.days && !!a.minutes;
    case "when":
      return !!a.when && (a.when !== "meals" || !!a.mealWalks?.length);
    case "have":
      return a.have !== undefined;
    case "joints":
      return a.joints !== undefined;
    case "jointHow":
      return Object.entries(a.joints ?? {}).every(
        ([k, v]) => !!v?.how && (!SIDED.includes(k as JointKey) || !!v.side)
      );
    case "falls":
      return !!a.falls && !!a.unsteady;
    case "body":
      return !!a.floor && (!chairAsked(S, a, today) || a.chairNoHands !== undefined || !!a.chairHelp);
    case "breathless":
      return !!a.breathless;
    case "style":
      return !!a.style;
    case "tests":
      return !!extra.testsDone;
  }
}

/** "15 of 15 answered" for the saved block; null before the questionnaire has been saved. */
export function answeredCount(
  S: KayaState,
  today = new Date(),
  asking: Asking & { safetyAnswered?: boolean } = {}
): { n: number; of: number } | null {
  const t = S.profile.training;
  if (!t || t.at === undefined) return null;
  const steps = trainingSteps(S, t, today, asking);
  const testsDone = latestTests(S) !== null;
  const n = steps.filter((s) => stepAnswered(S, t, s, today, { safetyAnswered: asking.safetyAnswered, testsDone })).length;
  return { n, of: steps.length };
}

/* ---------- the options, and what each one changes (the boards' words) ---------- */

export const BEFORE_ROWS: { key: "chest" | "faint" | "breath" | "supervised"; label: string }[] = [
  { key: "chest", label: "Chest pain or pressure" },
  { key: "faint", label: "Fainting, or dizziness that made you lose your balance" },
  { key: "breath", label: "Breathless with light effort, a racing heart, swollen ankles, or calf pain when walking" },
  { key: "supervised", label: "Has a doctor said you should exercise only with supervision?" },
];

export const RECENT_OPTS: Opt<string>[] = [
  { value: "operation", label: "An operation", sub: "Easy walks until your doctor says more" },
  { value: "fracture", label: "A fracture", sub: "Easy walks until your doctor says more" },
  { value: "heart", label: "A heart attack or stroke", sub: "Easy walks until your doctor says more" },
  { value: "fall", label: "A fall that hurt", sub: "Easy walks and the chair session first" },
];

export const GOAL_OPTS: Opt<TrainingGoal>[] = [
  { value: "weight", label: "Lose weight or waist", sub: "Walks on five days, building to 30–45 minutes" },
  { value: "strength", label: "Get stronger", sub: "Strength & balance three times a week" },
  { value: "balance", label: "Steadier on my feet", sub: "Balance work on three days, and before two walks" },
  { value: "sugar", label: "Help my blood sugar", sub: "A 10-minute walk after lunch and dinner; never two idle days" },
  { value: "bp", label: "Help my BP", sub: "Walks most days, slow breathing daily, holds counted out loud" },
  { value: "joints", label: "Ease joint pain", sub: "Strengthening for that joint, with a pain rule" },
  { value: "active", label: "Just keep moving", sub: "Morning joints daily, walks most days, strength twice" },
];

export const ACTIVE_OPTS: Opt<NonNullable<TrainingProfile["active"]>>[] = [
  { value: "none", label: "Hardly at all", sub: "Start with short sessions, sitting first" },
  { value: "walks", label: "Walk most days", sub: "Your walks count; strength starts at its easiest" },
  { value: "some", label: "Exercise 1–2 times a week", sub: "Start one step up" },
  { value: "regular", label: "3 or more times a week", sub: "Harder work only with no health condition" },
];

export const DAYS_OPTS: Opt<NonNullable<TrainingProfile["days"]>>[] = [
  { value: "2-3", label: "2–3" },
  { value: "4-5", label: "4–5" },
  { value: "6-7", label: "6–7" },
];

export const MINUTES_OPTS: { value: NonNullable<TrainingProfile["minutes"]>; label: string }[] = [
  { value: 10, label: "10" },
  { value: 20, label: "20" },
  { value: 30, label: "30" },
  { value: 45, label: "45" },
];

/** "6–7 days of 20 minutes: strength & balance on three days, walks on the others." */
export function daysLine(days: TrainingProfile["days"], minutes: TrainingProfile["minutes"]): string | null {
  if (!days || !minutes) return null;
  const d = DAYS_OPTS.find((o) => o.value === days)?.label ?? days;
  const shape =
    days === "2-3" ? "strength & balance on each of them" : "strength & balance on three days, walks on the others";
  const split = minutes === 10 ? " A 10-minute day can be two 10s." : "";
  return `${d} days of ${minutes} minutes: ${shape}.${split}`;
}

export const WHEN_OPTS: Opt<NonNullable<TrainingProfile["when"]>>[] = [
  { value: "morning", label: "Morning", sub: "A reminder at 7 am; in summer, the walk before 8" },
  { value: "meals", label: "After meals", sub: "A 10-minute walk after the meals you pick" },
  { value: "evening", label: "Evening", sub: "A reminder at 6 pm, never straight before bed" },
  { value: "varies", label: "It varies", sub: "No fixed reminder; Today offers the session" },
];

export const MEAL_WALK_OPTS: { value: "breakfast" | "lunch" | "dinner"; label: string }[] = [
  { value: "breakfast", label: "Breakfast" },
  { value: "lunch", label: "Lunch" },
  { value: "dinner", label: "Dinner" },
];

/** "after lunch and after dinner" */
function afterMeals(m: readonly string[]): string {
  return join(m.map((x) => `after ${x}`));
}

export function mealWalkLine(m: readonly string[] | undefined): string | null {
  if (!m?.length) return null;
  return m.length === 1
    ? `A walk ${afterMeals(m)}, and a reminder after it.`
    : `A walk ${afterMeals(m)}, and a reminder after each.`;
}

export const HAVE_OPTS: { value: HaveKey; label: string }[] = [
  { value: "chair", label: "Chair" },
  { value: "wall", label: "Wall" },
  { value: "bed", label: "Bed" },
  { value: "stairs", label: "Stairs" },
  { value: "treadmill", label: "Treadmill" },
  { value: "band", label: "Band" },
  { value: "dumbbells", label: "Dumbbells" },
  { value: "floor", label: "Floor space" },
];

/** "No band: rows use a towel pulled apart. Stairs: the step-up joins when you are ready." */
export function haveLine(have: readonly HaveKey[] | undefined): string | null {
  if (have === undefined) return null;
  const parts: string[] = [];
  if (!have.includes("band")) parts.push("No band: rows use a towel pulled apart.");
  if (have.includes("stairs")) parts.push("Stairs: the step-up joins when you are ready.");
  if (!have.includes("chair")) parts.push("No chair: the seated moves use the bed's edge.");
  return parts.length ? parts.join(" ") : null;
}

export const JOINT_OPTS: { value: JointKey; label: string }[] = [
  { value: "neck", label: "Neck" },
  { value: "shoulder", label: "Shoulder" },
  { value: "back", label: "Back" },
  { value: "hip", label: "Hip" },
  { value: "knee", label: "Knee" },
  { value: "ankle", label: "Ankle or foot" },
  { value: "wrist", label: "Wrist" },
];

export const JOINT_HOW_OPTS: { value: JointAnswer["how"]; label: string }[] = [
  { value: "mild", label: "Mild" },
  { value: "limits", label: "Limits what I do" },
  { value: "treated", label: "A doctor is treating it" },
];

export const SIDE_OPTS: { value: NonNullable<JointAnswer["side"]>; label: string }[] = [
  { value: "right", label: "Right" },
  { value: "left", label: "Left" },
  { value: "both", label: "Both" },
];

const JOINT_WORDS: Record<JointKey, string> = {
  neck: "neck",
  shoulder: "shoulder",
  back: "back",
  hip: "hip",
  knee: "knee",
  ankle: "ankle",
  wrist: "wrist",
};

/** What a joint's answer does, under its follow-up. */
export function jointLine(k: JointKey, v: Partial<JointAnswer> | undefined): string | null {
  const w = cap(JOINT_WORDS[k]);
  switch (v?.how) {
    case "mild":
      return `${w} moves as written, and they stop if the pain passes 2 out of 10.`;
    case "limits":
      return `${w} moves start at their easiest, and stop if the pain passes 2 out of 10 or is still there next morning.`;
    case "treated":
      return `Do what your physiotherapist set; Kaya leaves ${JOINT_WORDS[k]} moves out.`;
    default:
      return null;
  }
}

export const FALLS_OPTS: Opt<NonNullable<TrainingProfile["falls"]>>[] = [
  { value: "none", label: "No" },
  { value: "once", label: "Once", sub: "Steady: every standing move at the counter" },
  { value: "more", label: "More than once, or it hurt", sub: "Steady, and a word with your doctor" },
];

export const UNSTEADY_OPTS: Opt<NonNullable<TrainingProfile["unsteady"]>>[] = [
  { value: "no", label: "No" },
  { value: "sometimes", label: "Sometimes", sub: "Steady: balance at the counter, a hand always in reach" },
  { value: "often", label: "Often", sub: "Steady, and walks with someone beside you" },
];

export type ChairAnswer = "yes" | "hands" | "help";

export const CHAIR_OPTS: Opt<ChairAnswer>[] = [
  { value: "yes", label: "Yes" },
  { value: "hands", label: "Only with hands", sub: "A firm, higher chair; hands allowed" },
  { value: "help", label: "Not without help", sub: "A physiotherapist should start this with you" },
];

export function chairAnswer(a: TrainingDraftAnswers): ChairAnswer | undefined {
  if (a.chairHelp) return "help";
  if (a.chairNoHands === true) return "yes";
  if (a.chairNoHands === false) return "hands";
  return undefined;
}

export function chairPatch(v: ChairAnswer): Pick<TrainingAnswers, "chairNoHands" | "chairHelp"> {
  return v === "help" ? { chairNoHands: false, chairHelp: true } : { chairNoHands: v === "yes", chairHelp: undefined };
}

export const FLOOR_OPTS: Opt<NonNullable<TrainingProfile["floor"]>>[] = [
  { value: "easy", label: "Easily" },
  { value: "hold", label: "With something to hold", sub: "Floor moves use the bed or a chair" },
  { value: "no", label: "No, or rather not", sub: "Nothing on the floor" },
];

export const BREATHLESS_OPTS: Opt<NonNullable<TrainingProfile["breathless"]>>[] = [
  { value: "no", label: "No" },
  { value: "hurrying", label: "Only when I hurry", sub: "Walks at a pace you can talk at; no fast work" },
  { value: "rest", label: "Yes", sub: "Easy walks only, and a word with your doctor" },
];

export const STYLE_OPTS: Opt<NonNullable<TrainingProfile["style"]>>[] = [
  { value: "circuit", label: "A circuit", sub: "One move after another, short rests" },
  { value: "yoga", label: "Calm, yoga-style", sub: "Slow moves, held, with breathing" },
  { value: "mix", label: "A mix", sub: "Strength days as a circuit, joints and cool-down yoga-style" },
];

export const BP_CONTROL_OPTS: { value: NonNullable<TrainingProfile["bpControlled"]>; label: string }[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unsure", label: "Not sure" },
];

export const FEET_OPTS: { value: NonNullable<TrainingProfile["feet"]>; label: string }[] = [
  { value: "ok", label: "No" },
  { value: "numb", label: "Numb" },
  { value: "sore", label: "A sore" },
];

/* ---------- the safety items as rules (older-adults-guidelines.md §B) ---------- */

/** 160/90 or more softens; 180/110 or more stops the day (§B item 8). */
export const BP_SOFTEN = { sys: 160, dia: 90 } as const;
export const BP_STOP = { sys: 180, dia: 110 } as const;

export type Soften =
  | "noVigorous"
  | "noHeadsDown"
  | "chairBed"
  | "shoes"
  | "talkTest"
  | "standSlowly"
  | "afterMeal"
  | "noFlexion"
  | "noFloor"
  | "support"
  | "light";

export interface TrainingRules {
  /** the plan is held at easy walks and the gentle sessions: a safety yes, breathless at rest,
   *  or needing help to stand */
  held: boolean;
  /** the last BP reading is 180/110 or more: no session until a lower one */
  noSession: boolean;
  soften: Soften[];
  /** one line each, in the order the rules were met: what changed and why */
  lines: string[];
}

/**
 * What the safety answers do to a plan: each one gates, softens or sends to a doctor first, and
 * says so in one line. The programme templates (P2) read `soften` to swap or drop a slot; Train
 * reads `held` and `noSession`. Kaya never says "cleared", never uses heart rate as effort for
 * anyone on a BP or heart medicine, and never says holds are unsafe with high BP. (ADR-053)
 */
export function trainingRules(S: KayaState, device: DeviceAnswers = {}): TrainingRules {
  const t = S.profile.training ?? {};
  const e = S.profile.eating ?? {};
  const conds = e.conditions ?? [];
  const meds = e.meds ?? [];
  const soften = new Set<Soften>();
  const lines: string[] = [];
  let held = false;
  let noSession = false;

  if (device.trainingGate) {
    held = true;
    lines.push("Talk to your doctor first. Until then Kaya suggests easy walks and the gentle sessions.");
  }
  if (t.breathless === "rest") {
    held = true;
    lines.push("Breathless at your own pace: easy walks only, and a word with your doctor.");
  }
  if (t.chairHelp) {
    held = true;
    lines.push("You need help to stand from a chair: a physiotherapist should start this with you.");
  }
  if (conds.includes("bp")) {
    const r = t.bpReading;
    if (r && (r.sys >= BP_STOP.sys || r.dia >= BP_STOP.dia)) {
      noSession = true;
      lines.push(`Your last BP reading was ${r.sys}/${r.dia}: no session until a lower one, and ask your doctor.`);
    } else if (!r || r.sys >= BP_SOFTEN.sys || r.dia >= BP_SOFTEN.dia || t.bpControlled !== "yes") {
      soften.add("noVigorous");
      lines.push(
        t.bpControlled === "yes" && !r
          ? "High BP with no reading to go by: no fast work, and a longer cool-down."
          : "High BP not yet under control: no holds held hard and no fast work; ask your doctor."
      );
    }
  }
  if (meds.includes("bp")) {
    soften.add("talkTest");
    soften.add("standSlowly");
    lines.push("On BP tablets: effort by the talk test, not heart rate, and stand up slowly.");
  }
  if (e.hypoRisk) {
    soften.add("afterMeal");
    lines.push("On insulin or low-sugar tablets: sessions after a meal, never fasted.");
  }
  if (t.feet === "sore") {
    soften.add("chairBed");
    lines.push("A sore on the foot: chair and bed moves only until it heals.");
  } else if (t.feet === "numb") {
    soften.add("shoes");
    lines.push("Numb feet: shoes on for every standing move, and check your feet after.");
  }
  if (t.eyesTreated) {
    soften.add("noHeadsDown");
    soften.add("noVigorous");
    lines.push("Eye treatment for diabetes: no heads-down holds and no hard effort.");
  }
  if (conds.includes("osteoporosis")) {
    soften.add("noFlexion");
    lines.push("Osteoporosis: no loaded bending or twisting; the leg work stays.");
  }
  if (t.floor === "no") soften.add("noFloor");
  if ((t.falls && t.falls !== "none") || (t.unsteady && t.unsteady !== "no")) {
    soften.add("support");
    lines.push(
      t.falls === "more"
        ? "Falls: support in reach on every standing move — ask a doctor or physio for a falls check."
        : "Support in reach on every standing move."
    );
  }
  // ACSM 2015: inactive with heart, metabolic or kidney disease → light until a doctor agrees
  if ((conds.includes("heart") || conds.includes("diabetes") || conds.includes("kidney")) && t.active !== "regular") {
    soften.add("light");
    lines.push("Light effort until your doctor says moderate is fine.");
  }
  if (t.breathless === "hurrying") soften.add("noVigorous");
  return { held, noSession, soften: [...soften], lines };
}

/* ---------- the rules on a day's session (P9: they bite on Train, the session and the player) ---------- */

/** What a held plan keeps (plan §3.5): the walk day, the gentle sessions and rest. */
const GENTLE_KEYS: readonly SessionKey[] = ["CH", "MJ", "WK", "P", "Y", "R"];
/** The one session with fast work; the treadmill's fast phases are `SessionGate.easyTreadmill`. */
const FAST_KEYS: readonly SessionKey[] = ["B"];
/** What runs in place of a held session, and of the intervals. */
export const HELD_KEY: SessionKey = "CH";
export const NO_FAST_KEY: SessionKey = "C";

/**
 * Why fast work (B's intervals, the treadmill's brisk phases) is off for this person, in a few
 * words, or null: high BP not under control or no reading, eye treatment, breathless when hurrying,
 * light effort until a doctor agrees (each from `trainingRules`), or "not yet" to "used to hard
 * effort?" at 40–59 until it is answered yes (plan §3.9).
 */
export function fastWorkOff(S: KayaState, device: DeviceAnswers = {}, today = new Date()): string | null {
  const r = trainingRules(S, device);
  const t = S.profile.training ?? {};
  const conds = S.profile.eating?.conditions ?? [];
  if (t.eyesTreated) return "your eye treatment";
  if (r.soften.includes("noVigorous") && conds.includes("bp")) return "your BP";
  if (t.breathless === "hurrying") return "breathless when you hurry";
  if (r.soften.includes("light")) return "light effort until your doctor says moderate";
  if (t.vigorousOk === false && trainingBand(S, today) === "40-59") return "you said not yet to hard effort";
  return null;
}

export interface SessionGate {
  /** the session that runs today */
  key: SessionKey;
  /** the session it stands in for, or null when nothing changed */
  from: SessionKey | null;
  /** no session at all today (a BP reading of 180/110 or more) */
  stop: boolean;
  /** the treadmill runs its easy walk, not its brisk phases */
  easyTreadmill: boolean;
  /** one line: what changed and why; null when nothing did */
  line: string | null;
}

/**
 * The safety answers on one day's session (ADR-053's rules, made to bite — P9). A held plan (a yes
 * on the phone's safety screens, breathless at rest, help to stand) keeps walks and the gentle
 * sessions: anything else becomes the Chair session. A BP reading of 180/110 stops the day. Fast
 * work off swaps B's intervals for Core & mobility and the treadmill's brisk phases for its easy
 * walk. Derived each time, never stored: the day's ticks go to the key that ran. `device` is
 * `useDeviceAnswers()` — the safety screen's answer never leaves the phone.
 */
export function sessionGate(S: KayaState, key: SessionKey, device: DeviceAnswers = {}, today = new Date()): SessionGate {
  const r = trainingRules(S, device);
  const fast = fastWorkOff(S, device, today);
  const same = { key, from: null, stop: false, easyTreadmill: r.held || fast !== null, line: null };
  if (r.noSession) {
    const reading = S.profile.training?.bpReading;
    return {
      ...same,
      stop: true,
      easyTreadmill: true,
      line: `No session today: your last BP reading was ${reading?.sys}/${reading?.dia}. Ask your doctor, and tell Kaya a lower one.`,
    };
  }
  if (r.held && !GENTLE_KEYS.includes(key)) {
    return { ...same, key: HELD_KEY, from: key, line: "The Chair session today: talk to your doctor first, and tell Kaya when they say yes." };
  }
  if (fast && FAST_KEYS.includes(key)) {
    return { ...same, key: NO_FAST_KEY, from: key, line: `Core & mobility in place of the intervals: no fast work — ${fast}.` };
  }
  return same;
}

/** The treadmill row's line when its brisk phases are off, or null. */
export function easyTreadmillLine(S: KayaState, device: DeviceAnswers = {}, today = new Date()): string | null {
  const r = trainingRules(S, device);
  if (r.held) return "An easy walk only: talk to your doctor first";
  const fast = fastWorkOff(S, device, today);
  return fast ? `An easy walk, no brisk phases — ${fast}` : null;
}

/* ---------- "Still right?" — food's answers read back ---------- */

export type StillKey = "pregnant" | "conditions" | "meds" | "periods" | "sleep";

/**
 * The shared answers Plan my food holds, one sentence each, in the board's order. Each has a
 * Change: the matching food question in a sheet (periods: Body & goals). A question food never
 * asked this person is left out rather than guessed.
 */
export function stillRight(S: KayaState, today = new Date()): { key: StillKey; text: string }[] {
  const e = S.profile.eating;
  if (!e) return [];
  const out: { key: StillKey; text: string }[] = [];
  const years = age(S, today);
  const female = S.profile.sex === "female";
  if (female && e.pregnant && (years === null || (years >= PREGNANCY_AGES.from && years <= PREGNANCY_AGES.to)))
    out.push({
      key: "pregnant",
      text:
        e.pregnant === "no" ? "Not pregnant or breastfeeding." : e.pregnant === "pregnant" ? "You are pregnant." : "You are breastfeeding.",
    });
  const conds = e.conditions ?? [];
  if (e.conditionsPrivate) out.push({ key: "conditions", text: "Conditions not said." });
  else if (e.conditions !== undefined)
    out.push({ key: "conditions", text: conds.length ? `${cap(join(conds.map((c) => CONDITION_WORDS[c])))}.` : "No conditions." });
  if (e.meds !== undefined)
    out.push({ key: "meds", text: e.meds.length ? `${cap(join(e.meds.map((m) => MED_WORDS[m])))}.` : "No regular medicines." });
  if (female && S.profile.periodsStopped) out.push({ key: "periods", text: "Your periods have stopped." });
  if (e.sleep) {
    const sleep = e.sleep === "<6" ? "under 6 hours" : e.sleep === "6-7" ? "6–7 hours" : "7 hours or more";
    out.push({ key: "sleep", text: `You sleep ${sleep}; ${e.shift ? "you work shifts" : "no shift work"}.` });
  }
  return out;
}

/* ---------- the tests: moved to fitnessTests.ts (ADR-054), re-exported for the questionnaire ---------- */

export {
  RETEST_DAYS,
  TUG_LINE,
  chairStandLine,
  latestTests,
  placement,
  retestDue,
  testsOffered,
  type Placement,
  type TestKey,
} from "./fitnessTests";

/* ---------- the plan's one line (plan §3.3; P2's richer line replaces it) ---------- */

function joinItems(xs: string[]): string {
  return xs.length === 2 ? `${xs[0]} and ${xs[1]}` : xs.join(", ");
}

/**
 * The rule and its evidence in one line, from the answers alone and the tests when there are any
 * — never a grade: "Three strength-and-balance days and walks after meals: you stood 9 times in 30
 * seconds (the usual range at 65 starts at 11), and you have prediabetes." A held plan
 * says walking and why.
 */
export function planLine(
  S: KayaState,
  a: TrainingDraftAnswers,
  tests?: DayTests | null,
  device: DeviceAnswers = {},
  today = new Date()
): string {
  const e = S.profile.eating ?? {};
  const conds = e.conditions ?? [];
  if (device.trainingGate)
    return "Easy walks and the gentle sessions until your doctor says more: you answered yes under Before you start.";
  if (a.breathless === "rest") return "Easy walks only until a doctor has listened: you get breathless at your own pace.";
  if (a.chairHelp)
    return "Easy walks and the chair session until a physiotherapist starts this with you: you need help to stand from a chair.";
  if (e.pregnant === "pregnant")
    return "Walking and the gentle sessions: you are pregnant, and your midwife or doctor sets the rest.";

  const bp = conds.includes("bp");
  const sugar = conds.includes("diabetes") || conds.includes("prediabetes");
  const bpWords =
    a.bpControlled === "yes"
      ? `high BP, under control${(e.meds ?? []).includes("bp") ? " on tablets" : ""}`
      : "high BP not yet under control";
  const years = age(S, today);
  const programme = a.programme ?? (older(S, today) ? "steady" : "kaya");

  if (programme === "kaya" && !older(S, today)) {
    const soft = bp && a.bpControlled !== "yes";
    if (soft) return `The Kaya week without fast work: ${bpWords}.`;
    if (a.breathless === "hurrying") return "The Kaya week without fast work: you get breathless when you hurry.";
    return "The Kaya week as it runs, three sessions twice and a rest day: nothing in your answers asks for a change.";
  }

  const placed = tests ? placement(S, tests, today, a) : null;
  const fell = (a.falls !== undefined && a.falls !== "none") || (a.unsteady !== undefined && a.unsteady !== "no");
  const steady = placed ? placed.level === "steady" : fell;
  const goals = a.goals ?? [];

  const items: string[] = [];
  const walksFirst = !steady && goals.some((g) => g === "bp" || g === "weight" || g === "active");
  if (walksFirst) items.push(sugar ? "walks after meals" : "walking most days");
  items.push(steady ? "three strength-and-balance days" : a.days === "2-3" ? "strength on each of your days" : "strength three days");
  if (sugar && !walksFirst) items.push("walks after meals");
  if (bp && a.bpControlled === "yes" && goals.includes("bp") && !steady) items.push("a wall sit");

  const reasons: string[] = [];
  if (placed?.by === "chair" && tests?.chairStand !== undefined)
    reasons.push(
      `you stood ${tests.chairStand} times in 30 seconds (the usual range at ${years} starts at ${chairStandLine(S, today)})`
    );
  else if (placed?.by === "tandem")
    reasons.push(`you held a heel-to-toe stand for ${tests?.balanceSecs ?? 0} seconds, under the 10 that is the line`);
  else if (placed?.by === "tug") reasons.push(`the up-and-go took you ${tests?.tug} seconds`);
  else if ((!placed || placed.by === "falls" || placed.by === "unsteady") && fell)
    reasons.push(a.falls && a.falls !== "none" ? "you have fallen in the last year" : "you feel unsteady at times");
  // a condition is named only where it shaped an item: the line explains the plan, not the person
  if (items.includes("walks after meals")) reasons.push(conds.includes("diabetes") ? "you have diabetes" : "you have prediabetes");
  if (items.includes("a wall sit")) reasons.push(bpWords);
  if (!reasons.length) reasons.push(years !== null ? `you are ${years}` : "your answers");

  const why = reasons.length === 2 ? `${reasons[0]}, and ${reasons[1]}` : join(reasons);
  return `${cap(joinItems(items))}: ${why}.`;
}

/* ---------- the read-back (board TrainingReadBack) ---------- */

const GOAL_WORDS: Record<TrainingGoal, string> = {
  weight: "to lose weight or waist",
  strength: "to get stronger",
  balance: "to be steadier on your feet",
  sugar: "to help your blood sugar",
  bp: "to help your BP",
  joints: "to ease joint pain",
  active: "to keep moving",
};

const ACTIVE_WORDS: Record<NonNullable<TrainingProfile["active"]>, string> = {
  none: "You are hardly active now.",
  walks: "You walk most days.",
  some: "You exercise once or twice a week.",
  regular: "You exercise three or more times a week.",
};

const DAYS_WORDS: Record<NonNullable<TrainingProfile["days"]>, string> = {
  "2-3": "Two or three days a week",
  "4-5": "Four or five days a week",
  "6-7": "Six or seven days a week",
};

const HAVE_WORDS: Record<HaveKey, string> = {
  chair: "a chair",
  wall: "a wall",
  bed: "a bed",
  stairs: "stairs",
  treadmill: "a treadmill",
  band: "a band",
  dumbbells: "dumbbells",
  floor: "floor space",
};

function jointSentence(k: JointKey, v: JointAnswer): string {
  const both = v.side === "both";
  const who =
    both ? `Both your ${JOINT_WORDS[k]}s` : v.side ? `Your ${v.side} ${JOINT_WORDS[k]}` : `Your ${JOINT_WORDS[k]}`;
  const verb =
    v.how === "limits"
      ? both
        ? "limit what you do"
        : "limits what you do"
      : v.how === "mild"
        ? `${both ? "are" : "is"} a little sore`
        : `${both ? "are" : "is"} being treated by a doctor`;
  return `${who} ${verb}.`;
}

/**
 * The answers as sentences before anything is saved, in five groups: what you want; how active,
 * how often and when; what you have and your joints; falls, the chair and the floor; breath and
 * style. A question this person was not asked is left out.
 */
export function readBack(S: KayaState, a: TrainingDraftAnswers, today = new Date()): string[] {
  const out: string[] = [];
  const g = a.goals ?? [];
  if (g.length === 1) out.push(`You want ${GOAL_WORDS[g[0]]}.`);
  else if (g.length >= 2) out.push(`You want ${GOAL_WORDS[g[0]]} first, then ${GOAL_WORDS[g[1]]}.`);

  {
    const parts: string[] = [];
    if (a.active) parts.push(ACTIVE_WORDS[a.active]);
    if (a.days) {
      let s = DAYS_WORDS[a.days];
      if (a.minutes) s += `, about ${a.minutes} minutes`;
      if (a.when === "meals" && a.mealWalks?.length) s += `, after ${join(a.mealWalks)}`;
      else if (a.when === "morning") s += ", in the morning";
      else if (a.when === "evening") s += ", in the evening";
      else if (a.when === "varies") s += ", when it suits";
      parts.push(`${s}.`);
    }
    if (parts.length) out.push(parts.join(" "));
  }

  {
    const parts: string[] = [];
    if (a.have !== undefined)
      parts.push(a.have.length ? `You have ${join(a.have.map((h) => HAVE_WORDS[h]))}.` : "You have nothing set aside to train with.");
    if (a.joints !== undefined) {
      const ticked = Object.entries(a.joints).filter((e): e is [JointKey, JointAnswer] => !!e[1]?.how);
      if (!ticked.length && !Object.keys(a.joints).length) parts.push("No joint limits you.");
      for (const [k, v] of ticked) parts.push(jointSentence(k, v));
    }
    if (parts.length) out.push(parts.join(" "));
  }

  {
    const parts: string[] = [];
    if (a.falls && a.unsteady) {
      const fell =
        a.falls === "none"
          ? "You have not fallen this year"
          : a.falls === "once"
            ? "You fell once this year"
            : "You have fallen more than once this year, or it hurt";
      const steady =
        a.unsteady === "no" ? "feel steady" : a.unsteady === "sometimes" ? "sometimes feel unsteady" : "often feel unsteady";
      const contrast = (a.falls === "none") !== (a.unsteady === "no");
      parts.push(`${fell} ${contrast ? "but" : "and"} ${steady}.`);
    }
    const chair = chairAsked(S, a, today) ? chairAnswer(a) : undefined;
    const floor =
      a.floor === "easy"
        ? "get down to the floor and up easily"
        : a.floor === "hold"
          ? "get to the floor with something to hold"
          : a.floor === "no"
            ? "keep off the floor"
            : "";
    const chairWords =
      chair === "yes"
        ? "You stand from a chair without your hands"
        : chair === "hands"
          ? "You stand from a chair with your hands"
          : chair === "help"
            ? "You need help to stand from a chair"
            : "";
    if (chairWords && floor) parts.push(`${chairWords}, and ${floor}.`);
    else if (chairWords) parts.push(`${chairWords}.`);
    else if (floor) parts.push(`You ${floor}.`);
    if (parts.length) out.push(parts.join(" "));
  }

  {
    const parts: string[] = [];
    if (a.breathless === "no") parts.push("Not breathless at your own pace.");
    else if (a.breathless === "hurrying") parts.push("Breathless only when you hurry.");
    else if (a.breathless === "rest") parts.push("Breathless even at your own pace.");
    if (a.style) {
      const style = a.style === "circuit" ? "A circuit" : a.style === "yoga" ? "Calm, yoga-style moves" : "A mix of styles";
      const voice = a.voice ? `, with ${a.lang === "hi" ? "a Hindi voice" : "a voice"} in the player` : ", no voice in the player";
      parts.push(`${style}${voice}.`);
    }
    if (parts.length) out.push(parts.join(" "));
  }
  return out;
}

export interface TrainingChanges {
  week: string;
  today: string;
  retest: string;
}

/** The read-back's "What changes" rows, from the answers and the tests when there are any. */
export function whatChanges(
  S: KayaState,
  a: TrainingDraftAnswers,
  tested: { ds: string; tests: DayTests } | null,
  device: DeviceAnswers = {},
  today = new Date()
): TrainingChanges {
  const conds = S.profile.eating?.conditions ?? [];
  const sugar = conds.includes("diabetes") || conds.includes("prediabetes");
  const meals = a.when === "meals" && a.mealWalks?.length ? a.mealWalks : sugar ? ["lunch", "dinner"] : null;
  const programme = a.programme ?? (older(S, today) ? "steady" : "kaya");
  const week = device.trainingGate || a.breathless === "rest" || a.chairHelp
    ? "Easy walks and the gentle sessions, until you change your answer"
    : programme === "steady"
      ? `Strength & balance on Mon, Wed and Fri; ${meals ? `walks ${afterMeals(meals)}` : "walks"} on the other days`
      : "Your sessions as they are";
  return {
    week,
    today: meals ? `A 10-minute walk ${afterMeals(meals)}` : "Today’s session, as planned",
    retest: tested ? `${dayLabel(addDays(tested.ds, RETEST_DAYS))}, four weeks on` : "After your first tests",
  };
}

/** You › Training's hero line. */
export function programmeLine(S: KayaState, today = new Date()): string {
  const t = S.profile.training;
  const programme = t?.programme ?? (older(S, today) ? "steady" : "kaya");
  if (programme === "kaya") return "Three sessions, twice a week, and a rest day.";
  const meals = t?.when === "meals" && t.mealWalks?.length ? t.mealWalks : null;
  return `Strength & balance on Mon, Wed and Fri; ${meals ? `walks after ${join(meals)}` : "walks most days"}.`;
}
